import { Box } from '@mui/material';
import React, { useMemo } from 'react';

import { INVENTORY_ERROR_TOOLTIP_TEXT } from '../../../constants/oneLink';
import { useCurrency } from '../../../contexts/CurrencyProvider';
import { IOneLinkErrors, OneLinkChunksEnum } from '../../../contexts/OneLinkSummaryProvider';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { getSingleFulfillmentCenterId, hasMskuItem } from '../../../utils/inventories';
import { getMarketplaceCartItems, getMarketplacePickOptions, getWarehouseItems } from '../../../utils/marketplaceSend';
import { CampaignPreview, CampaignViewItem, FCDetails, InventoryViewItem } from '../../index';
import MarketplaceSendItemView from '../../MarketplaceSendItemView/MarketplaceSendItemView';
import { Section } from './common';

interface IProps {
  instance: IOneLinkWithCampaignSummaries | null;
  onEdit?: () => void;
  errors?: IOneLinkErrors;
}

export const InventorySection: React.FC<IProps> = ({ instance, errors, onEdit }) => {
  const { getCampaignTotal } = useCurrency();

  const inventoryErrors = useMemo(
    () => ({
      items: errors?.[OneLinkChunksEnum.InventoryInfo]?.items,
      pick_campaign_ids: errors?.[OneLinkChunksEnum.InventoryInfo]?.pick_campaign_ids,
      pre_created_engagement_id: errors?.[OneLinkChunksEnum.InventoryInfo]?.pre_created_engagement_id,
    }),
    [errors],
  );

  const { items, pick_campaigns } = useMemo(
    () =>
      instance ?? {
        items: [],
        pick_campaigns: [],
      },
    [instance],
  );

  const marketplaceOptions = useMemo(() => getMarketplacePickOptions(instance), [instance]);
  const marketplaceCartItems = useMemo(() => getMarketplaceCartItems(instance), [instance]);

  const isPYG = useMemo(
    () => !!pick_campaigns?.length || marketplaceOptions.length > 0,
    [pick_campaigns, marketplaceOptions],
  );

  const itemsLayout = useMemo(() => {
    const inventoryRows = getWarehouseItems(items).map((item) => {
      const { item_id: itemId } = item;

      return <InventoryViewItem key={itemId} item={item} showCount={false} showQuantity defaultView />;
    });

    const marketplaceRows = marketplaceCartItems.map((item) => (
      <MarketplaceSendItemView key={item.product_id} item={item} showQuantity defaultView />
    ));

    const rows = [...inventoryRows, ...marketplaceRows];

    return rows.length ? rows : null;
  }, [items, marketplaceCartItems]);

  const campaignsLayout = useMemo(() => {
    const campaignRows = (pick_campaigns ?? []).map((item) => {
      const { box_id: boxId, name, icon_url: iconUrl, items: campaignItems } = item;
      const hasMsku = hasMskuItem(campaignItems);

      return (
        <CampaignViewItem
          key={boxId}
          defaultView
          name={name}
          hasMsku={hasMsku}
          price={getCampaignTotal(item)}
          imageUrl={iconUrl}
          details={<FCDetails fulfillmentCenterId={getSingleFulfillmentCenterId(campaignItems)} />}
          hint={<CampaignPreview showPreview defaultView campaign={item} showWarning />}
        />
      );
    });

    const optionRows = marketplaceOptions.map((item) => (
      <MarketplaceSendItemView key={item.product_id} item={item} defaultView />
    ));

    const rows = [...campaignRows, ...optionRows];

    return rows.length ? rows : null;
  }, [pick_campaigns, marketplaceOptions, getCampaignTotal]);

  const title = useMemo(() => {
    switch (true) {
      case !!isPYG:
        return 'Campaigns selected';
      case !!instance?.campaign_name:
        return instance?.campaign_name;
      default:
        return 'Custom send';
    }
  }, [isPYG, instance?.campaign_name]);

  const inventoryError = useMemo(() => {
    return inventoryErrors?.items || inventoryErrors?.pick_campaign_ids || inventoryErrors?.pre_created_engagement_id;
  }, [inventoryErrors?.items, inventoryErrors?.pick_campaign_ids, inventoryErrors?.pre_created_engagement_id]);

  return (
    <Section
      tooltipText={INVENTORY_ERROR_TOOLTIP_TEXT}
      title={title}
      minHeight={420}
      onEdit={onEdit}
      errors={inventoryError}
    >
      <Box sx={{ maxHeight: 330, overflowY: 'auto' }}>{isPYG ? campaignsLayout : itemsLayout}</Box>
    </Section>
  );
};
